import type { UploadFile } from "antd/es/upload";
import type {
	IOfficeHours,
	IReleaseTicketResult,
	IRenderedMacro,
	ISpamReport,
	ISpamSettings,
	ISupportMacro,
	ITicketAutoCloseSettings,
} from "types/support-ticket.types";
import { api } from "utils/axios";

export interface JiraProject {
	id: string;
	key: string;
	name: string;
}

export interface JiraIssueType {
	id: string;
	name: string;
	subtask: boolean;
}

export interface JiraUser {
	accountId: string;
	displayName: string;
	emailAddress?: string;
}

/** What one project + issue type's create screen actually accepts. */
export interface JiraCreateFields {
	priorities: Array<{ id: string; name: string }>;
	defaultPriorityId: string | null;
	supportsPriority: boolean;
	supportsLabels: boolean;
	supportsAssignee: boolean;
}

export interface JiraDraft {
	summary: string;
	description: string;
	issueTypeName: string | null;
	priorityName: string | null;
	labels: string[];
	/** `fallback` means the ticket's own text, NOT a model draft. Shown to the user. */
	source: "gemini" | "fallback";
	fallbackReason?: string;
}

export interface CreateJiraIssuePayload {
	projectKey?: string;
	issueType?: string;
	issueTypeId?: string;
	summary?: string;
	description?: string;
	labels?: string[];
	assigneeAccountId?: string;
	priorityId?: string;
}

// Admin endpoints
export const supportTicketApi = {
	// Ticket plugins (external links). Mounted on their own prefix because
	// admin/support-tickets/:id would swallow a literal segment like "plugins".
	getPluginReadiness: () =>
		api.get("admin/ticket-plugins/readiness").then((r) => r.data),
	getTicketLinks: (id: number) =>
		api.get(`admin/ticket-plugins/tickets/${id}/links`).then((r) => r.data),
	createJiraIssue: (id: number, data?: CreateJiraIssuePayload) =>
		api
			.post(`admin/ticket-plugins/tickets/${id}/links/jira`, data ?? {})
			.then((r) => r.data),

	// Create-screen metadata. Every one of these asks Jira with the SAME
	// credentials the create call will use, so the pickers can only ever offer
	// something those credentials can actually act on.
	getJiraProjects: (query?: string) =>
		api
			.get<JiraProject[]>("admin/ticket-plugins/jira/projects", {
				params: query ? { query } : undefined,
			})
			.then((r) => r.data),
	getJiraIssueTypes: (projectKey: string) =>
		api
			.get<JiraIssueType[]>(
				`admin/ticket-plugins/jira/projects/${encodeURIComponent(projectKey)}/issue-types`,
			)
			.then((r) => r.data),
	getJiraCreateFields: (projectKey: string, issueTypeId: string) =>
		api
			.get<JiraCreateFields>(
				`admin/ticket-plugins/jira/projects/${encodeURIComponent(projectKey)}/issue-types/${encodeURIComponent(issueTypeId)}/fields`,
			)
			.then((r) => r.data),
	getJiraAssignableUsers: (projectKey: string, query?: string) =>
		api
			.get<JiraUser[]>(
				`admin/ticket-plugins/jira/projects/${encodeURIComponent(projectKey)}/assignable-users`,
				{ params: query ? { query } : undefined },
			)
			.then((r) => r.data),
	getJiraLabels: () =>
		api.get<string[]>("admin/ticket-plugins/jira/labels").then((r) => r.data),
	/**
	 * Gemini-drafted summary and description. Always resolves — the server falls
	 * back to the ticket's own text and reports `source: "fallback"` rather than
	 * failing, so a drafting outage cannot block an escalation.
	 */
	draftJiraIssue: (
		id: number,
		body: {
			allowedIssueTypes?: string[];
			allowedPriorities?: string[];
			knownLabels?: string[];
		},
	) =>
		api
			.post<JiraDraft>(`admin/ticket-plugins/jira/tickets/${id}/draft`, body)
			.then((r) => r.data),

	// Tickets
	getTickets: (params?: Record<string, any>) =>
		api.get("admin/support-tickets", { params }).then((r) => r.data),
	getTicket: (id: number) =>
		api.get(`admin/support-tickets/${id}`).then((r) => r.data),
	createTicket: (data: any) =>
		api.post("admin/support-tickets", data).then((r) => r.data),
	updateTicket: (id: number, data: any) =>
		api.patch(`admin/support-tickets/${id}`, data).then((r) => r.data),
	deleteTicket: (id: number) =>
		api.delete(`admin/support-tickets/${id}`).then((r) => r.data),
	assignTicket: (id: number, userId: number) =>
		api
			.post(`admin/support-tickets/${id}/assign`, { userId })
			.then((r) => r.data),
	unassignTicket: (id: number) =>
		api.post(`admin/support-tickets/${id}/unassign`).then((r) => r.data),
	changeStatus: (id: number, status: string) =>
		api
			.post(`admin/support-tickets/${id}/status`, { status })
			.then((r) => r.data),
	getMessages: (id: number) =>
		api.get(`admin/support-tickets/${id}/messages`).then((r) => r.data),
	addMessage: (id: number, data: any) =>
		api.post(`admin/support-tickets/${id}/messages`, data).then((r) => r.data),
	bulkUpdate: (data: any) =>
		api.post("admin/support-tickets/bulk/update", data).then((r) => r.data),
	bulkDelete: (ticketIds: number[]) =>
		api
			.post<{ deleted: number }>("admin/support-tickets/bulk/delete", {
				ticketIds,
			})
			.then((r) => r.data),

	/**
	 * Moves a quarantined ticket to New. Safe to repeat: inside an hour the api
	 * re-queues the two notifications, after that it answers `already_released`.
	 */
	releaseTicket: (id: number) =>
		api
			.post<IReleaseTicketResult>(`admin/support-tickets/${id}/release`)
			.then((r) => r.data),

	// Spam handling (EQ-2366)
	getSpamSettings: () =>
		api
			.get<ISpamSettings>("admin/support-tickets/spam-settings")
			.then((r) => r.data),
	updateSpamSettings: (
		data: Partial<Pick<ISpamSettings, "mode" | "llmEnabled">>,
	) =>
		api
			.put<ISpamSettings>("admin/support-tickets/spam-settings", data)
			.then((r) => r.data),
	getSpamReport: () =>
		api
			.get<ISpamReport>("admin/support-tickets/spam-settings/report")
			.then((r) => r.data),

	// Dashboard
	getDashboardStats: () =>
		api.get("admin/support-tickets/dashboard/stats").then((r) => r.data),
	getSlaMetrics: () =>
		api.get("admin/support-tickets/dashboard/sla").then((r) => r.data),

	// Categories
	getCategories: () =>
		api.get("admin/support-tickets/categories").then((r) => r.data),
	createCategory: (data: any) =>
		api.post("admin/support-tickets/categories", data).then((r) => r.data),
	updateCategory: (id: number, data: any) =>
		api
			.patch(`admin/support-tickets/categories/${id}`, data)
			.then((r) => r.data),
	deleteCategory: (id: number) =>
		api.delete(`admin/support-tickets/categories/${id}`).then((r) => r.data),

	// SLA Config
	getSlaConfigs: () =>
		api.get("admin/support-tickets/sla-config").then((r) => r.data),
	updateSlaConfig: (priority: string, data: any) =>
		api
			// POST, matching the API's @Post("sla-config/:priority"). This was
			// PUT, so every SLA save 404'd.
			.post(`admin/support-tickets/sla-config/${priority}`, data)
			.then((r) => r.data),

	// Macros (ADR 0026)
	getMacros: () => api.get("admin/support-tickets/macros").then((r) => r.data),
	createMacro: (data: Pick<ISupportMacro, "title" | "body" | "kind">) =>
		api.post("admin/support-tickets/macros", data).then((r) => r.data),
	updateMacro: (
		id: number,
		data: Partial<Pick<ISupportMacro, "title" | "body" | "kind">>,
	) =>
		api.patch(`admin/support-tickets/macros/${id}`, data).then((r) => r.data),
	deleteMacro: (id: number) =>
		api.delete(`admin/support-tickets/macros/${id}`).then((r) => r.data),
	/** Filled in on the server from this ticket only; the call sends no body. */
	renderMacro: (ticketId: number, macroId: number): Promise<IRenderedMacro> =>
		api
			.post(`admin/support-tickets/${ticketId}/macros/${macroId}/render`)
			.then((r) => r.data),

	// Office hours (ADR 0026)
	getOfficeHours: () =>
		api.get("admin/support-tickets/office-hours").then((r) => r.data),
	updateOfficeHours: (data: IOfficeHours) =>
		api.put("admin/support-tickets/office-hours", data).then((r) => r.data),

	// Auto-close settings
	getAutoCloseSettings: () =>
		api.get("admin/support-tickets/auto-close-settings").then((r) => r.data),
	updateAutoCloseSettings: (data: Partial<ITicketAutoCloseSettings>) =>
		api
			.post("admin/support-tickets/auto-close-settings", data)
			.then((r) => r.data),

	// User endpoints
	getMyTickets: () => api.get("user/support-tickets").then((r) => r.data),
	getMyTicket: (id: number) =>
		api.get(`user/support-tickets/${id}`).then((r) => r.data),
	getMyTicketMessages: (id: number) =>
		api.get(`user/support-tickets/${id}/messages`).then((r) => r.data),
	replyToMyTicket: (id: number, data: any) =>
		api.post(`user/support-tickets/${id}/messages`, data).then((r) => r.data),
	closeMyTicket: (id: number) =>
		api.post(`user/support-tickets/${id}/close`).then((r) => r.data),
	createMyTicket: (data: {
		subject: string;
		description: string;
		priority?: string;
		categoryId?: number;
		files?: string[];
	}) => api.post("user/support-tickets", data).then((r) => r.data),
	getMyCategories: () =>
		api.get("user/support-tickets/categories").then((r) => r.data),
	/**
	 * In-product report. Note what is NOT in this payload: no `source`, no
	 * `tags`, no `priority`. The server derives all three from `kind` + `impact`
	 * so a client cannot route its own ticket or grant itself an SLA exemption.
	 */
	createIssueReport: (data: {
		kind: "bug" | "feature";
		subject: string;
		description: string;
		impact?: string;
		files?: string[];
		route?: string;
		appVersion?: string;
		sentryEventId?: string;
		sentryReplayId?: string;
		posthogSessionId?: string;
		viewportWidth?: number;
		viewportHeight?: number;
		userAgent?: string;
		clientTime?: string;
	}) => api.post("user/support-tickets/report", data).then((r) => r.data),
	/**
	 * Returns three shapes for the same upload:
	 * - `urls` — signed, **~15 min TTL**. Display only. Persisting one leaves a
	 *   permanently dead link once it expires.
	 * - `storageKeys` — the stable bucket keys. **This is what you post back**
	 *   when creating a ticket or message; the API stores keys and re-signs them
	 *   on every read.
	 * - `files` — key + url + original filename, when you need all three.
	 */
	uploadTicketFiles: (files: File[]) => {
		const formData = new FormData();
		files.forEach((file) => {
			formData.append("files", file);
		});
		return api
			.post<{
				urls: string[];
				storageKeys: string[];
				files: Array<{ url: string; storageKey: string; filename: string }>;
			}>("user/support-tickets/upload", formData, {
				headers: { "Content-Type": "multipart/form-data" },
			})
			.then((r) => r.data);
	},
};

/** Matches the server's FilesInterceptor("files", 5) cap. */
export const TICKET_ATTACHMENT_MAX_COUNT = 5;

/**
 * Turn an antd `Upload` fileList into the `files[]` a ticket or message
 * endpoint wants. Every attach surface (create ticket, report issue, admin
 * reply, customer reply) needs the same two steps in the same order, and the
 * second one is easy to get wrong:
 *
 *   1. upload the raw Files, then
 *   2. post back `storageKeys` — **never `urls`**.
 *
 * `urls` are signed with a ~15 min TTL. The API's `normalizeIncomingFiles`
 * keeps a ref it cannot parse verbatim, so a signed URL is written to the DB
 * and the attachment is permanently dead a quarter of an hour later. That
 * exact bug reached production once; this helper exists so the decision is
 * made in one place instead of at every call site.
 *
 * Returns `[]` for an empty list without calling the API.
 */
export async function uploadTicketAttachments(
	fileList: UploadFile[],
): Promise<string[]> {
	const files = fileList.map((f) => f.originFileObj).filter(Boolean) as File[];
	if (files.length === 0) return [];

	const result = await supportTicketApi.uploadTicketFiles(files);
	return result.storageKeys ?? result.urls;
}
